import { CommonRootState } from '@common/types/common-root-state-type.d';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// --- Begin: Imports from the provided mock ---
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, List } from "lucide-react"; // Keep ArrowUpRight if needed elsewhere
// --- End: Imports from the provided mock ---

import { DBAutomationSessionThunks, DBBotThunks, DBKnowledgeBaseThunks } from '@common/reducers/DBServiceThunks';
// Import other necessary thunks...

// --- End: Imports for Data Fetching & Selection ---

// --- Begin: Import new sub-components ---
import PageLayout from '@/common-components/page-layout/page-layout';
import { useAppDispatch } from '@/common/reducers/hooks/store.hook';
import { selectUserRole } from '@/common/reducers/UserSelectors';
import {
    selectDashboardLastUpdated,
    selectDashboardLoading
} from './BotUsageDashboardSelectors';
import { KnowledgeBaseAdoptionTab } from './components/KnowledgeBaseAdoptionTab';
import { NodeUsageTab } from './components/NodeUsageTab';
import { OverviewStats } from './components/OverviewStats';
import { NodeEventDistributionTab } from './components/NodeEventDistributionTab';
import {
    fetchAllDashboardStats,
    fetchDashboardOverviewStats,
    fetchFeatureAdoption,
    fetchKbAdoption,
    fetchNodeTypeDistribution
} from './dashboardStatsSlice';

/**
 * BotUsageDashboard Component
 *
 * Displays bot usage and adoption statistics. Accessible only by users with the 'admin' role.
 * Redirects non-admin users to the home page.
 */
const BotUsageDashboard: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const userRole = useSelector((state: CommonRootState) => selectUserRole(state));
    
    // Select dashboard loading state and last updated timestamp
    const isLoading = useSelector(selectDashboardLoading);
    const lastUpdated = useSelector(selectDashboardLastUpdated);

    // --- Begin: Fetch Data ---
    useEffect(() => {
        if (userRole === 'admin') {
            console.log('Fetching dashboard data...');
            
            // Fetch DB data for local calculations (as fallbacks)
            dispatch(DBBotThunks.find({ query: {} }));
            dispatch(DBAutomationSessionThunks.find({ query: {} }));
            dispatch(DBKnowledgeBaseThunks.find({ query: {} }));
            
            // Fetch dashboard statistics from the API
            // Option 1: Fetch all stats in one call
            dispatch(fetchAllDashboardStats({ startDate: null, endDate: null }))
                .unwrap()
                .then(data => {
                    console.log('All dashboard stats loaded:', data);
                })
                .catch(error => {
                    console.error('Error loading dashboard stats:', error);
                    
                    // If the combined endpoint fails, try individual endpoints
                    fetchIndividualStats();
                });
        }
    }, [dispatch, userRole]);

    // Function to fetch individual stat endpoints
    const fetchIndividualStats = () => {
        // Fetch overview stats
        dispatch(fetchDashboardOverviewStats({ startDate: null, endDate: null }))
            .unwrap()
            .then(data => console.log('Overview stats loaded:', data))
            .catch(error => console.error('Error loading overview stats:', error));

        // Fetch node type distribution
        dispatch(fetchNodeTypeDistribution({ startDate: null, endDate: null }))
            .unwrap()
            .then(data => console.log('Node type distribution loaded:', data))
            .catch(error => console.error('Error loading node type distribution:', error));

        // Fetch advanced feature adoption
        dispatch(fetchFeatureAdoption({ startDate: null, endDate: null }))
            .unwrap()
            .then(data => console.log('Feature adoption loaded:', data))
            .catch(error => console.error('Error loading feature adoption:', error));

        // Fetch knowledge base adoption
        dispatch(fetchKbAdoption({ startDate: null, endDate: null }))
            .unwrap()
            .then(data => console.log('KB adoption loaded:', data))
            .catch(error => console.error('Error loading KB adoption:', error));
    };
    // --- End: Fetch Data ---

    const timeRangeOptions = ["Last 7 days", "Last 30 days", "Last 90 days", "Year to date", "All time"];
    
    // Handle refresh button click
    const handleRefresh = () => {
        dispatch(fetchAllDashboardStats({ startDate: null, endDate: null }));
    };

    useEffect(() => {
        if (userRole !== 'admin') {
            console.warn('User does not have admin privileges. Redirecting to home.');
            navigate('/');
        }
    }, [userRole, navigate]);


    if (userRole !== 'admin') {
        return null; // Render nothing while redirecting
    }

    // Render the dashboard content if the user is an admin
    return (
        <PageLayout title="Bot System Adoption Dashboard">
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <p className="text-slate-500">Monitor platform usage and adoption metrics</p>
                        {lastUpdated && (
                            <p className="text-xs text-slate-400 mt-1">
                                Last updated: {new Date(lastUpdated).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <Select defaultValue="Last 30 days">
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Select time range" />
                            </SelectTrigger>
                            <SelectContent>
                                {timeRangeOptions.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button 
                            variant="outline" 
                            className="flex items-center gap-2" 
                            onClick={handleRefresh}
                            disabled={isLoading}
                        >
                            <Download className="h-4 w-4" />
                            {isLoading ? 'Loading...' : 'Refresh'}
                        </Button>
                        <Button 
                            variant="default" 
                            className="flex items-center gap-2" 
                            onClick={() => navigate('/bots')}
                        >
                            <List className="h-4 w-4" />
                            View All Bots
                        </Button>
                    </div>
                </div>

                {/* Overview stats section with current snapshot data */}
                <OverviewStats />

                {/* Main dashboard tabs for different views */}
                <Tabs defaultValue="nodes" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="nodes">Node Usage</TabsTrigger>
                        <TabsTrigger value="kb">Knowledge Base Adoption</TabsTrigger>
                        <TabsTrigger value="events">Event Distribution</TabsTrigger>
                    </TabsList>

                    <TabsContent value="nodes" className="space-y-4">
                        <NodeUsageTab />
                    </TabsContent>

                    <TabsContent value="kb" className="space-y-4">
                        <KnowledgeBaseAdoptionTab />
                    </TabsContent>

                    <TabsContent value="events" className="space-y-4">
                        <NodeEventDistributionTab />
                    </TabsContent>
                </Tabs>
            </div>
        </PageLayout>
    );
};

export default BotUsageDashboard; 